import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useHistory } from 'react-router';
import ReCAPTCHA from 'react-google-recaptcha';
import mapValues from 'lodash/mapValues';
import trim from 'lodash/trim';
import Footer from '@shared/footer/footer';
import { useTranslation } from 'react-i18next';
import {
  FormType,
  FormValuesInterface,
  ValuesToSendInterface
} from '../../types/form.types';
import getFormValidationSchema from './contact-form.schema';
import useReasons from '../../hooks/use-reasons.hook';
import usePostForm from '../../network/hooks/post-form.hook';
import useConvertFile from '../../hooks/use-convert-file.hook';
import ContactFormContent from './contact-form.content';
import ContactFormComponents from './contact-form.components';
import useFields from '../../hooks/use-fields.hook';
import useQueryParams from '../../hooks/use-query-params.hook';
import { useParamsContext } from '../../params/params-context';

interface ContactFormProps {
  type: FormType;
}
const { StyledForm } = ContactFormComponents;

const ContactForm: React.FunctionComponent<ContactFormProps> = ({ type }) => {
  const { utm: utmQuery, setReasonsFromQuestion, question } = useQueryParams();
  const [isFormVisible, setIsFormVisible] = useState<boolean>(!!question);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const {
    mainReason,
    detailReason,
    isReasonFilled,
    showAttachment
  } = useReasons();
  const getConvertedFile = useConvertFile();
  const postFormData = usePostForm();
  const history = useHistory();
  const { utm, pages, portalType } = useParamsContext();
  const {
    resetAllValues,
    allFields,
    noAttachmentFields,
    errorMessage,
    isLoading,
    case_reason,
    case_detailed_reason
  } = useFields();
  const { t } = useTranslation();
  const fields = !showAttachment ? noAttachmentFields : allFields;

  const methods = useForm({
    resolver: yupResolver(
      getFormValidationSchema(
        {
          case_reason,
          case_detailed_reason,
          ...fields
        },
        portalType,
        t
      )
    ),
    mode: 'onChange'
  });

  const utmParams = useCallback((): { [p: string]: string } | undefined => {
    return type === 'client_prospects' ? { ...utmQuery, ...utm } : undefined;
  }, [utmQuery, utm, type]);

  const reasons = useMemo(() => {
    if (!mainReason) {
      return [];
    }
    return !detailReason ? [mainReason] : [mainReason, detailReason];
  }, [detailReason, mainReason]);

  const showFormHandler = useCallback((): void => {
    setIsFormVisible(true);
  }, []);

  const onSubmit = useCallback(
    async (data: FormValuesInterface): Promise<void> => {
      const recaptchaToken = recaptchaRef.current?.getValue();
      const fixedData: ValuesToSendInterface = {
        ...mapValues(data, trim),
        type,
        attachment:
          data.attachment && data.attachment[0]
            ? await getConvertedFile(data.attachment[0])
            : undefined,
        utm: utmParams()
      };
      if (postFormData && recaptchaToken) {
        setIsErrorVisible(false);
        await postFormData(fixedData, recaptchaToken)
          .then(() => {
            switch (true) {
              case pages?.success_client_prospects &&
                type === 'client_prospects':
                window.location.replace(pages?.success_client_prospects || '');
                break;
              case pages?.success_partner_prospects &&
                type === 'partner_prospects':
                window.location.replace(pages?.success_partner_prospects || '');
                break;
              case typeof pages?.success === 'string':
                window.location.replace(pages?.success || '');
                break;
              default:
                history.push('success');
                break;
            }
          })
          .catch(() => {
            setIsErrorVisible(true);
          });
      }
    },
    [getConvertedFile, history, postFormData, utmParams, type, pages]
  );

  useEffect(() => {
    if (fields) {
      setReasonsFromQuestion(methods);
    }
  }, [fields, methods, setReasonsFromQuestion]);

  useEffect(() => {
    return (): void => {
      resetAllValues();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <ContactFormContent
          isReasonFilled={isReasonFilled}
          reasons={reasons}
          showFormHandler={showFormHandler}
          isFormVisible={isFormVisible}
          isErrorVisible={isErrorVisible}
          formFields={fields}
          isLoading={isLoading}
          errorMessage={errorMessage}
          type={type}
          ref={recaptchaRef}
        />
      </StyledForm>
      {(isFormVisible || (!reasons[0] && !isLoading)) && (
        <Footer type={type} portalType={portalType} />
      )}
    </FormProvider>
  );
};

export default ContactForm;
